/**
 * Lead capture schemas (issue #20, spec §17 Contact Forms and Lead Capture).
 *
 * Used by:
 *   - the API to validate `POST /api/public/contact` request bodies,
 *   - the public CONTACT_FORM block renderer / vanilla JS form to keep field
 *     names in lockstep with the server.
 *
 * Honeypot field (`_hp`):
 *   The form ships a hidden `_hp` input whose presence indicates a bot
 *   submission. We accept it through Zod so the body parses cleanly, but the
 *   handler short-circuits with a 204 (deceptive — we silently absorb the
 *   submission so bots can't tell their attempt was rejected). The zod schema
 *   allows the field to be a string or omitted; the handler decides what to
 *   do based on emptiness.
 *
 * Field-length caps:
 *   We cap each free-text field at conservative lengths to defend against
 *   abuse (a 10MB `message` would saturate the Lead table fast). The caps are
 *   well above any realistic legitimate submission.
 *
 * `formJson`:
 *   The raw form payload as submitted, kept opaque for forward-compat. Spec
 *   §17 lists Submitted fields, UTM, referrer, etc. — we capture them all in
 *   `formJson` and lift the canonical fields (name/email/...) onto their own
 *   columns for fast querying. Limits the depth/size to keep abuse contained.
 */

import { z } from "zod";

const trimmedShortText = z.string().trim().max(200);
const trimmedLongText = z.string().trim().max(5000);
const trimmedEmailLike = z.string().trim().max(320); // RFC 5321 max email length

/**
 * The shape of `formJson`. Permissive: we accept any JSON-serialisable
 * key/value record so the platform doesn't need a schema migration when a
 * site's form template adds a new field. Bounded depth and size are enforced
 * by the request body size cap (`express.json({ limit: '1mb' })` in
 * `app.ts`).
 */
const formJsonSchema = z.record(z.string(), z.unknown());

export const contactSubmissionSchema = z.object({
  siteId: z.string().min(1, "siteId is required"),
  pageId: z.string().min(1).optional(),
  /**
   * The raw form payload. We store this verbatim in the Lead row's `formJson`
   * column for forward-compat with form templates the platform can't predict.
   */
  formJson: formJsonSchema,
  // Canonical fields are optional — a form that only collects an email is
  // valid. The handler does NOT enforce "at least one of name/email/message"
  // because spec §17 says the operator can configure which fields are
  // required on a per-form basis; the public renderer enforces that on the
  // client side via the HTML `required` attribute.
  name: trimmedShortText.optional(),
  email: trimmedEmailLike.optional(),
  company: trimmedShortText.optional(),
  phone: trimmedShortText.optional(),
  message: trimmedLongText.optional(),
  visitorId: trimmedShortText.optional(),
  sessionId: trimmedShortText.optional(),
  experimentVariantId: trimmedShortText.optional(),
  /**
   * Honeypot trap. We use a Zod-permissive shape (string or omitted) so the
   * body parses cleanly; the route handler decides whether the value is
   * "empty" (legitimate) or "non-empty" (bot — silently absorb).
   */
  _hp: z.string().optional(),
});

export type ContactSubmissionInput = z.infer<typeof contactSubmissionSchema>;
